/**
 * Dot-path value access, shared by the mapper, columns, search and sort.
 *
 * This is deliberately the only place in the library that knows how to turn
 * `'metadata.owner'` into a value, so all four features stay consistent.
 */

/** Split a dot path into segments, tolerating `a.b[0].c` style indices. */
function segments(path: string): string[] {
  return path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((s) => s.length > 0);
}

/**
 * Read a nested value by dot path. Returns `undefined` for any missing link in
 * the chain rather than throwing, because consumer data is untrusted.
 */
export function getValueAtPath(source: unknown, path: string): unknown {
  if (source == null) return undefined;
  // Fast path: no nesting.
  if (!path.includes('.') && !path.includes('[')) {
    return (source as Record<string, unknown>)[path];
  }
  let current: unknown = source;
  for (const key of segments(path)) {
    if (current == null) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/** True when the path resolves to something other than `undefined`. */
export function hasValueAtPath(source: unknown, path: string): boolean {
  return getValueAtPath(source, path) !== undefined;
}
